import { type ReactNode, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { ChevronRight } from 'lucide';

// common
import Icon from 'common/components/Icon';
import useIsGuest from 'common/hook/useIsGuest';

// screen
import {
  useQuickSearchChannels,
  useQuickSearchClassifications,
  useQuickSearchGroups,
} from 'modules/quick-search/services/useQuickSearchTaxonomy';
import useQuickSearchSuppliers from 'modules/quick-search/services/useQuickSearchSuppliers';
import ChannelChips from 'modules/quick-search/components/ChannelChips';
import CategoryCard, { CategoryCardSkeleton } from 'modules/quick-search/components/CategoryCard';
import SupplierResultTable from 'modules/quick-search/components/SupplierResultTable';
import type { QsChannel, QsClassification, QsGroup } from 'modules/quick-search/types';

const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 2,
};

const isMainMealChannel = (channel: QsChannel) =>
  channel.name.localeCompare('Bữa chính', 'vi', { sensitivity: 'base' }) === 0;

const QuickSearchPage = () => {
  const [sp, setSp] = useSearchParams();
  const channelId = Number(sp.get('kpp')) || undefined;
  const classificationId = Number(sp.get('plhh')) || undefined;
  const groupId = Number(sp.get('nmh')) || undefined;
  const isGuest = useIsGuest();
  // Khách ở bước danh sách HSNL: theo Figma node 7237-28440 màn này đổi tiêu đề và bỏ khối
  // lọc (chip KPP + Xóa bộ lọc + breadcrumb drill) — chỉ còn thanh công cụ và bảng.
  const guestSupplierList = isGuest && !!groupId;

  const { data: channels = [], isLoading: loadingChannels } = useQuickSearchChannels();
  const { data: classifications = [], isFetching: loadingCls } =
    useQuickSearchClassifications(channelId);
  const { data: groups = [], isFetching: loadingGrp } = useQuickSearchGroups(
    channelId,
    classificationId
  );
  // Tải cả nhóm để Tìm kiếm/Bộ lọc/phân trang client-side (KIOS chưa có param search/filter).
  const { data: suppliersResp, isFetching: loadingSup } = useQuickSearchSuppliers({
    groupId,
    perPage: 100,
  });

  // Cập nhật state lọc trên URL (để "Quay lại" từ chi tiết giữ nguyên bộ lọc).
  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSp(next);
  };

  const selectChannel = (ch: QsChannel) =>
    update({ kpp: String(ch.id), plhh: undefined, nmh: undefined, page: undefined });
  const selectClassification = (c: QsClassification) =>
    update({ plhh: String(c.id), nmh: undefined, page: undefined });
  const selectGroup = (g: QsGroup) => update({ nmh: String(g.id), page: undefined });

  // Vào màn chưa chọn KPP thì gắn sẵn Bữa chính (replace để nút Back không quay về trạng thái trống).
  useEffect(() => {
    if (channelId || channels.length === 0) return;
    const mainMeal = channels.find(isMainMealChannel);
    if (!mainMeal) return;
    const next = new URLSearchParams(sp);
    next.set('kpp', String(mainMeal.id));
    setSp(next, { replace: true });
  }, [channelId, channels, setSp, sp]);

  const selectedChannel = channels.find((c) => c.id === channelId);
  const selectedClassification = classifications.find((c) => c.id === classificationId);
  const selectedGroup = groups.find((g) => g.id === groupId);

  const renderGrid = (
    items: (QsClassification | QsGroup)[],
    onClick: (item: any) => void,
    loading: boolean,
    renderCount?: (item: any) => ReactNode
  ) => {
    if (loading && items.length === 0)
      return (
        <Box sx={gridSx} aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <CategoryCardSkeleton key={i} />
          ))}
        </Box>
      );
    if (items.length === 0)
      return (
        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', p: 2 }}>
          <FormattedMessage id="emptyData" />
        </Typography>
      );
    return (
      <Box sx={gridSx}>
        {items.map((item) => (
          <CategoryCard
            key={item.id}
            image={item.image}
            title={item.name}
            description={item.description}
            countLabel={renderCount ? renderCount(item) : undefined}
            onClick={() => onClick(item)}
          />
        ))}
      </Box>
    );
  };

  return (
    <Paper elevation={0} className="paper-list" sx={{ p: 2.5 }}>
      {/*
        KHÔNG render tiêu đề + breadcrumb ở đây: header (DefaultBreadcrumb) đã hiện sẵn tiêu đề
        trang theo tên chức năng "Quick Search" và breadcrumb "Cổng thông tin > Quick Search".
        Thêm ở trong thẻ nữa thành hai tiêu đề và hai breadcrumb chồng nhau.
      */}

      {!guestSupplierList && (
        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <ChannelChips
            channels={channels}
            selectedId={channelId}
            onSelect={selectChannel}
            loading={loadingChannels}
          />
        </Box>
      )}

      {/* Breadcrumb drill: KPP > PLHH > NMH (bấm để quay lại cấp trên) */}
      {!guestSupplierList && selectedChannel && (
        <Breadcrumbs separator={<Icon icon={ChevronRight} size={16} />} sx={{ mt: 2 }}>
          <Link
            component="button"
            underline="hover"
            color={classificationId ? 'primary' : 'text.primary'}
            onClick={() => update({ plhh: undefined, nmh: undefined, page: undefined })}
          >
            {selectedChannel.name}
          </Link>
          {selectedClassification && (
            <Link
              component="button"
              underline="hover"
              color={groupId ? 'primary' : 'text.primary'}
              onClick={() => update({ nmh: undefined, page: undefined })}
            >
              {selectedClassification.name}
            </Link>
          )}
          {selectedGroup && <Typography color="text.primary">{selectedGroup.name}</Typography>}
        </Breadcrumbs>
      )}

      {/* Nội dung theo cấp đã chọn */}
      <Box sx={{ mt: 2 }}>
        {!channelId && !loadingChannels && !channels.some(isMainMealChannel) && (
          <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', p: 2 }}>
            <FormattedMessage id="quickSearch.selectChannel" />
          </Typography>
        )}
        {channelId &&
          !classificationId &&
          renderGrid(classifications, selectClassification, loadingCls, (c: QsClassification) => (
            <FormattedMessage id="quickSearch.groupCount" values={{ count: c.groupCount }} />
          ))}
        {channelId &&
          classificationId &&
          !groupId &&
          renderGrid(groups, selectGroup, loadingGrp, (g: QsGroup) => (
            <FormattedMessage id="quickSearch.supplierCount" values={{ count: g.supplierCount }} />
          ))}
        {groupId && (
          <SupplierResultTable
            suppliers={suppliersResp?.results ?? []}
            isLoading={loadingSup}
            totalAvailable={suppliersResp?.meta?.total}
          />
        )}
      </Box>
    </Paper>
  );
};

export default QuickSearchPage;
